"use strict";
/**
 * PNL reporting API — bet-performance history.
 *
 * Browser → proxy server (/pnl/*) → exchange history APIs. Both venues are
 * normalized server-side into the shared shapes in ../types/pnl.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.getPnlEvents = getPnlEvents;
exports.getPnlDetail = getPnlDetail;
exports.getPnlTotals = getPnlTotals;
exports.getPnlUsers = getPnlUsers;
exports.recordUserPriceSample = recordUserPriceSample;
const request_1 = require("./request");
/**
 * One page of the operator's traded events for a venue, grouped by game. League
 * is optional — omit (empty string) for all leagues. The list defaults to the
 * last 48h of activity; `cursor` (a window token from the previous page's
 * nextCursor) widens the window to load more history.
 */
async function getPnlEvents(venue, league, cursor, 
/** Owner only: answer as this user (ai/PNL_TOTALS_PLAN.md §20). */
viewAs) {
    const params = { venue };
    if (league)
        params.league = league;
    if (cursor)
        params.cursor = cursor;
    if (viewAs)
        params.viewAs = viewAs;
    const res = await (0, request_1.apiRequest)("GET", "/pnl/events", {
        params,
    });
    return {
        events: res.events ?? [],
        nextCursor: res.nextCursor,
        exhausted: res.exhausted ?? true,
    };
}
/**
 * Aggregate PNL + trade list for one event. `marketIds` (the traded markets
 * from the list row) scope the PM-US query to just those markets — without
 * them the server would enumerate the event's entire market catalog.
 */
async function getPnlDetail(venue, eventId, marketIds, 
/** Owner only: answer as this user. */
viewAs) {
    const params = { venue, eventId };
    if (marketIds && marketIds.length > 0)
        params.marketIds = marketIds.join(",");
    if (viewAs)
        params.viewAs = viewAs;
    const res = await (0, request_1.apiRequest)("GET", "/pnl/detail", {
        params,
    });
    return res.detail;
}
/**
 * Period PnL over an arbitrary range, optionally split by time and dimension.
 * The range filters by REALIZATION date, not by when the bet was placed. Check
 * `partial` and `uncheckedMarkets` before presenting a figure as complete.
 */
async function getPnlTotals(query) {
    const params = {
        from: query.from,
        to: query.to,
    };
    if (query.venue)
        params.venue = query.venue;
    if (query.bucket)
        params.bucket = query.bucket;
    if (query.groupBy && query.groupBy.length > 0)
        params.groupBy = query.groupBy.join(",");
    if (query.scope)
        params.scope = query.scope;
    if (query.viewAs)
        params.viewAs = query.viewAs;
    if (query.userId)
        params.userId = query.userId;
    if (query.tz)
        params.tz = query.tz;
    const res = await (0, request_1.apiRequest)("GET", "/pnl/totals", { params });
    return {
        buckets: res.buckets ?? [],
        totals: res.totals,
        legRoles: res.legRoles ?? [],
        dataFrom: res.dataFrom ?? null,
        partial: res.partial ?? false,
        settlementCoverage: res.settlementCoverage ?? 1,
        uncheckedMarkets: res.uncheckedMarkets ?? 0,
        truncated: res.truncated ?? false,
    };
}
/** Owner only: everyone the PnL page can be viewed as — names, never a phone. */
async function getPnlUsers() {
    const res = await (0, request_1.apiRequest)("GET", "/pnl/users");
    return res.users ?? [];
}
/**
 * Record the operator-invoked price sample for one click (POST
 * /order-log/price-sample — see src/server/routes/order-log/).
 *
 * Not a reporting read like the rest of this module, but it belongs to the same
 * domain: it writes the datapoint the PNL page's EV column reads back.
 *
 * Errors surface as ApiRequestError so the caller can distinguish a terminal
 * outcome (400 / 404 / 409 — disarm) from a transient one (5xx — stay armed).
 */
async function recordUserPriceSample(request) {
    const res = await (0, request_1.apiRequest)("POST", "/order-log/price-sample", { body: request });
    return {
        success: res.success ?? false,
        sampled: res.sampled ?? 0,
        skipped: res.skipped ?? 0,
    };
}
